import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { 
  Plus, Calendar, Users, Receipt, CarSimple, ForkKnife,
  MagnifyingGlass, PencilSimple, Trash, Eye, Check, X,
  EnvelopeSimple, DownloadSimple, Money, Warning, CheckCircle,
  Upload, Clock, ArrowRight, User, WhatsappLogo, Bell, BellSlash, ChartBar,
  Bed, MagicWand, ArrowsClockwise, UserCircle, FilePdf
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Events = ({ user }) => {
  // Data states
  const [dashboardStats, setDashboardStats] = useState({});
  const [events, setEvents] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [transportBookings, setTransportBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Selection states
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [openDialog, setOpenDialog] = useState(null);
  
  // Filter states
  const [eventFilter, setEventFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [transportEventFilter, setTransportEventFilter] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('');
  
  // Import state
  const fileInputRef = useRef(null);
  const [importEventId, setImportEventId] = useState(null);
  
  // Bulk invoice state
  const [reviewInvoices, setReviewInvoices] = useState(null);
  const [reviewEventId, setReviewEventId] = useState(null);
  
  // Edit invoice state
  const [editingInvoice, setEditingInvoice] = useState(null);
  
  // Room allocation state
  const [rooms, setRooms] = useState([]);
  const [unassignedParticipants, setUnassignedParticipants] = useState([]);
  const [roomEventFilter, setRoomEventFilter] = useState('');
  const [allocating, setAllocating] = useState(false);
  const [draggedParticipant, setDraggedParticipant] = useState(null);
  const [roomConfig, setRoomConfig] = useState({ room_capacity: 2, strict_gender_matching: true });
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashRes, eventsRes, participantsRes, invoicesRes, driversRes, bookingsRes, roomsRes] = await Promise.all([
        api.get('/events/dashboard'),
        api.get('/events'),
        api.get('/events/participants/all'),
        api.get('/events/invoices'),
        api.get('/drivers'),
        api.get('/events/transport-bookings'),
        api.get('/events/rooms/all')
      ]);
      
      setDashboardStats(dashRes.data);
      setEvents(eventsRes.data);
      setParticipants(participantsRes.data);
      setInvoices(invoicesRes.data);
      setDrivers(driversRes.data);
      setTransportBookings(bookingsRes.data);
      setRooms(roomsRes.data);
    } catch (error) {
      toast.error('Failed to load events data');
    } finally {
      setLoading(false);
    }
  };

  // Filtered participants
  const filteredParticipants = useMemo(() => {
    return participants.filter(p => {
      const matchesEvent = !eventFilter || p.event_id === eventFilter;
      const matchesStatus = !statusFilter || p.invoice_status === statusFilter;
      const matchesSearch = !searchTerm || 
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.email.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesEvent && matchesStatus && matchesSearch;
    });
  }, [participants, eventFilter, statusFilter, searchTerm]);

  // Event handlers
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/events', {
        name: formData.get('name'),
        start_date: formData.get('start_date'),
        end_date: formData.get('end_date'),
        description: formData.get('description'),
        venue: formData.get('venue'),
        account_name: formData.get('account_name') || null,
        account_number: formData.get('account_number') || null,
        bank_name: formData.get('bank_name') || null
      });
      toast.success('Event created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.put(`/events/${editingItem.id}`, {
        name: formData.get('name'),
        start_date: formData.get('start_date'),
        end_date: formData.get('end_date'),
        description: formData.get('description'),
        venue: formData.get('venue'),
        status: formData.get('status'),
        account_name: formData.get('account_name') || null,
        account_number: formData.get('account_number') || null,
        bank_name: formData.get('bank_name') || null
      });
      toast.success('Event updated');
      setOpenDialog(null);
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event and all its participants?')) return;
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Event deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleCreateParticipant = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const eventId = formData.get('event_id');
    try {
      await api.post(`/events/${eventId}/participants`, {
        event_id: eventId,
        first_name: formData.get('first_name'),
        last_name: formData.get('last_name'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        arrival_date: formData.get('arrival_date'),
        departure_date: formData.get('departure_date'),
        needs_transportation: formData.get('needs_transportation') === 'on',
        gender: formData.get('gender') || null,
        is_couple: formData.get('is_couple') === 'on'
      });
      toast.success('Participant added');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to add participant');
    }
  };

  const handleUpdateParticipant = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.put(`/events/participants/${editingItem.id}`, {
        first_name: formData.get('first_name'),
        last_name: formData.get('last_name'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        arrival_date: formData.get('arrival_date'),
        departure_date: formData.get('departure_date'),
        needs_transportation: formData.get('needs_transportation') === 'on',
        gender: formData.get('gender') || null,
        is_couple: formData.get('is_couple') === 'on'
      });
      toast.success('Participant updated');
      setOpenDialog(null);
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update participant');
    }
  };

  const handleDeleteParticipant = async (participantId) => {
    if (!window.confirm('Delete this participant?')) return;
    try {
      await api.delete(`/events/participants/${participantId}`);
      toast.success('Participant deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete participant');
    }
  };

  // Room Management Functions
  const loadRoomsForEvent = useCallback(async (eventId) => {
    if (!eventId) {
      setRooms([]);
      setUnassignedParticipants([]);
      return;
    }
    try {
      const [roomsRes, unassignedRes] = await Promise.all([
        api.get(`/events/rooms/all?event_id=${eventId}`),
        api.get(`/events/rooms/unassigned-participants/${eventId}`)
      ]);
      setRooms(roomsRes.data);
      setUnassignedParticipants(unassignedRes.data);
    } catch (error) {
      toast.error('Failed to load room data');
    }
  }, []);

  useEffect(() => {
    if (roomEventFilter) {
      loadRoomsForEvent(roomEventFilter);
    }
  }, [roomEventFilter, loadRoomsForEvent]);

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/events/rooms', {
        event_id: formData.get('event_id'),
        room_name: formData.get('room_name'),
        capacity: parseInt(formData.get('capacity'))
      });
      toast.success('Room created');
      setOpenDialog(null);
      loadRoomsForEvent(roomEventFilter);
    } catch (error) {
      toast.error('Failed to create room');
    }
  };

  const handleBulkCreateRooms = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await api.post('/events/rooms/bulk-create', null, {
        params: {
          event_id: formData.get('event_id'),
          count: parseInt(formData.get('count')),
          capacity: parseInt(formData.get('capacity')),
          prefix: formData.get('prefix') || 'Room'
        }
      });
      toast.success(`Created ${response.data.created} rooms`);
      setOpenDialog(null);
      loadRoomsForEvent(roomEventFilter);
    } catch (error) {
      toast.error('Failed to create rooms');
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Delete this room? Occupants will be unassigned.')) return;
    try {
      await api.delete(`/events/rooms/${roomId}`);
      toast.success('Room deleted');
      loadRoomsForEvent(roomEventFilter);
    } catch (error) {
      toast.error('Failed to delete room');
    }
  };

  const handleAutoAllocate = async () => {
    if (!roomEventFilter) {
      toast.error('Please select an event first');
      return;
    }
    setAllocating(true);
    try {
      const response = await api.post(`/events/rooms/auto-allocate/${roomEventFilter}`, roomConfig);
      toast.success(`Allocated ${response.data.results.total_allocated} participants to ${response.data.results.rooms_used} rooms`);
      loadRoomsForEvent(roomEventFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to auto-allocate');
    } finally {
      setAllocating(false);
    }
  };

  const handleClearAllocations = async () => {
    if (!roomEventFilter) return;
    if (!window.confirm('Clear all room assignments for this event?')) return;
    try {
      await api.delete(`/events/rooms/clear-all/${roomEventFilter}`);
      toast.success('All allocations cleared');
      loadRoomsForEvent(roomEventFilter);
    } catch (error) {
      toast.error('Failed to clear allocations');
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (participant) => {
    setDraggedParticipant(participant);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDropOnRoom = async (roomId, fromRoomId = null) => {
    if (!draggedParticipant) return;
    try {
      await api.post('/events/rooms/assign-participant', {
        participant_id: draggedParticipant.id,
        from_room_id: fromRoomId,
        to_room_id: roomId
      });
      toast.success('Participant assigned to room');
      loadRoomsForEvent(roomEventFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign participant');
    } finally {
      setDraggedParticipant(null);
    }
  };

  const handleUnassignParticipant = async (participantId) => {
    try {
      await api.post(`/events/rooms/unassign-participant/${participantId}`);
      toast.success('Participant unassigned');
      loadRoomsForEvent(roomEventFilter);
    } catch (error) {
      toast.error('Failed to unassign participant');
    }
  };

  const handleDownloadRoomReport = async () => {
    if (!roomEventFilter) {
      toast.error('Please select an event first');
      return;
    }
    try {
      const response = await api.get(`/events/rooms/report/${roomEventFilter}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('Room report generated');
    } catch (error) {
      toast.error('Failed to generate room report');
    }
  };

  const handleDownloadMealsReport = async (eventId) => {
    try {
      const response = await api.get(`/events/meals/report/${eventId}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('Meals report generated');
    } catch (error) {
      toast.error('Failed to generate meals report');
    }
  };

  // CSV Import
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !importEventId) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const participants = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          if (values.length < 4) continue;
          
          const participant = {};
          headers.forEach((header, index) => {
            if (header.includes('first')) participant.first_name = values[index];
            else if (header.includes('last')) participant.last_name = values[index];
            else if (header.includes('email')) participant.email = values[index];
            else if (header.includes('phone')) participant.phone = values[index];
            else if (header.includes('arrival')) participant.arrival_date = values[index];
            else if (header.includes('departure')) participant.departure_date = values[index];
            else if (header.includes('transport')) participant.needs_transportation = values[index].toLowerCase() === 'yes';
          });
          
          if (participant.first_name && participant.email) {
            participants.push(participant);
          }
        }
        
        const response = await api.post(`/events/${importEventId}/participants/import`, participants);
        toast.success(`Imported ${response.data.count} participants`);
        setOpenDialog(null);
        setImportEventId(null);
        loadData();
      } catch (error) {
        toast.error('Failed to import participants');
      }
    };
    reader.readAsText(file);
  };

  // Email actions
  const handleSendMealLink = async (participantId) => {
    try {
      await api.post(`/events/participants/${participantId}/send-meal-link`);
      toast.success('Meal selection link sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleSendAllMealLinks = async (eventId) => {
    try {
      const response = await api.post(`/events/${eventId}/send-all-meal-links`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send meal links');
    }
  };

  // Invoice actions
  const handleGenerateInvoice = async (participantId) => {
    try {
      await api.post(`/events/participants/${participantId}/generate-invoice`);
      toast.success('Invoice generated');
      loadData();
    } catch (error) {
      toast.error('Failed to generate invoice');
    }
  };

  const handleSendInvoice = async (participantId) => {
    try {
      await api.post(`/events/participants/${participantId}/send-invoice`);
      toast.success('Invoice sent');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const handlePreviewInvoice = (participantId) => {
    // Open PDF in new tab for preview
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${baseUrl}/api/events/participants/${participantId}/preview-invoice?token=${token}`, '_blank');
  };

  // Bulk invoice handlers
  const handleGenerateAllInvoices = async (eventId) => {
    try {
      const response = await api.post(`/events/${eventId}/generate-all-invoices`);
      toast.success(response.data.message);
      loadData();
      // Open review dialog
      handleReviewInvoices(eventId);
    } catch (error) {
      toast.error('Failed to generate invoices');
    }
  };

  const handleReviewInvoices = async (eventId) => {
    try {
      const response = await api.get(`/events/${eventId}/invoices-review`);
      setReviewInvoices(response.data);
      setReviewEventId(eventId);
      setOpenDialog('reviewInvoices');
    } catch (error) {
      toast.error('Failed to load invoices for review');
    }
  };

  const handleSendAllInvoices = async () => {
    if (!reviewEventId) return;
    if (!window.confirm('Send invoices to all participants with pending invoices?')) return;
    
    try {
      const response = await api.post(`/events/${reviewEventId}/send-all-invoices`);
      toast.success(response.data.message);
      setOpenDialog(null);
      setReviewInvoices(null);
      setReviewEventId(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoices');
    }
  };

  // Edit invoice handler
  const handleUpdateInvoice = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.put(`/events/invoices/${editingInvoice.id}`, {
        room_nights: parseInt(formData.get('room_nights')),
        room_rate: parseFloat(formData.get('room_rate')),
        meal_count: parseInt(formData.get('meal_count')),
        meal_rate: parseFloat(formData.get('meal_rate')),
        transportation_needed: formData.get('transportation_needed') === 'on',
        transportation_rate: parseFloat(formData.get('transportation_rate')),
        amount_paid: parseFloat(formData.get('amount_paid'))
      });
      toast.success('Invoice updated');
      setOpenDialog(null);
      setEditingInvoice(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    }
  };

  // Resend invoice after edit
  const handleResendInvoice = async (participantId) => {
    try {
      await api.post(`/events/participants/${participantId}/send-invoice`);
      toast.success('Invoice resent');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invoice');
    }
  };

  // Toggle invoice reminders
  const handleToggleReminders = async (invoiceId) => {
    try {
      const response = await api.put(`/events/invoices/${invoiceId}/toggle-reminders`);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle reminders');
    }
  };

  // Send all due reminders
  const handleSendDueReminders = async () => {
    if (!window.confirm('Send payment reminders to all eligible participants?')) return;
    try {
      const response = await api.post('/events/invoices/send-reminders');
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await api.post(`/events/participants/${selectedParticipant.id}/record-payment`, {
        participant_id: selectedParticipant.id,
        amount: parseFloat(formData.get('amount')),
        payment_method: formData.get('payment_method'),
        reference: formData.get('reference')
      });
      toast.success(`Payment recorded! Receipt ${response.data.receipt_number} sent to participant`);
      setOpenDialog(null);
      setSelectedParticipant(null);
      loadData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  // Driver handlers
  const handleCreateDriver = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/drivers', {
        name: formData.get('name'),
        phone: formData.get('phone'),
        status: formData.get('status') || 'available'
      });
      toast.success('Driver added');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to add driver');
    }
  };

  const handleUpdateDriver = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.put(`/drivers/${editingItem.id}`, {
        name: formData.get('name'),
        phone: formData.get('phone'),
        status: formData.get('status')
      });
      toast.success('Driver updated');
      setOpenDialog(null);
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update driver');
    }
  };

  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm('Delete this driver?')) return;
    try {
      await api.delete(`/drivers/${driverId}`);
      toast.success('Driver deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete driver');
    }
  };

  // Transport booking handlers
  const handleGenerateTransportBookings = async (eventId) => {
    try {
      const response = await api.post(`/events/${eventId}/generate-transport-bookings`);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error('Failed to generate transport bookings');
    }
  };

  const handleAssignDriver = async (bookingId, driverId) => {
    try {
      await api.put(`/events/transport-bookings/${bookingId}/assign-driver?driver_id=${driverId}`);
      toast.success('Driver assigned');
      loadData();
    } catch (error) {
      toast.error('Failed to assign driver');
    }
  };

  const handleCompleteBooking = async (bookingId) => {
    try {
      await api.put(`/events/transport-bookings/${bookingId}/complete`);
      toast.success('Booking completed');
      loadData();
    } catch (error) {
      toast.error('Failed to complete booking');
    }
  };

  // WhatsApp helper
  const openWhatsApp = (phone, booking) => {
    const cleanPhone = phone.replace(/[\s-]/g, '');
    const message = encodeURIComponent(
      `Hi! You have a transport booking:\n\n` +
      `Guest: ${booking.participant_name}\n` +
      `Type: ${booking.booking_type.toUpperCase()}\n` +
      `Date: ${booking.booking_date}\n` +
      `Event: ${booking.event_name}\n` +
      `Guest Phone: ${booking.participant_phone}\n\n` +
      `Please confirm your availability.`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  // Filtered transport bookings
  const filteredBookings = useMemo(() => {
    return transportBookings.filter(b => {
      return !transportEventFilter || b.event_id === transportEventFilter;
    });
  }, [transportBookings, transportEventFilter]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEventStatusColor = (status) => {
    return status === 'open' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B2560D]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#2B3210]">Events Management</h1>
          <p className="text-sm text-[#505631]">Manage events, participants, and invoicing</p>
        </div>
      </div>

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="bg-[#FBF8EF] border border-[#E5E2D9] flex-wrap">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-white">
            <ChartBar size={16} className="mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="events" className="data-[state=active]:bg-white">
            <Calendar size={16} className="mr-2" />
            Events
          </TabsTrigger>
          <TabsTrigger value="participants" className="data-[state=active]:bg-white">
            <Users size={16} className="mr-2" />
            Participants
          </TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-white">
            <Receipt size={16} className="mr-2" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="transportation" className="data-[state=active]:bg-white">
            <CarSimple size={16} className="mr-2" />
            Transportation
          </TabsTrigger>
          <TabsTrigger value="drivers" className="data-[state=active]:bg-white">
            <User size={16} className="mr-2" />
            Drivers
          </TabsTrigger>
          <TabsTrigger value="rooms" className="data-[state=active]:bg-white">
            <Bed size={16} className="mr-2" />
            Rooms
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-[#B2560D] bg-opacity-10 rounded-lg">
                    <Calendar size={24} className="text-[#B2560D]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#505631]">Active Events</p>
                    <p className="text-2xl font-bold text-[#2B3210]">{dashboardStats.active_events || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Users size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#505631]">Total Participants</p>
                    <p className="text-2xl font-bold text-[#2B3210]">{dashboardStats.total_participants || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <Receipt size={24} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#505631]">Pending Invoices</p>
                    <p className="text-2xl font-bold text-[#2B3210]">{dashboardStats.pending_invoices || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <Money size={24} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#505631]">Outstanding</p>
                    <p className="text-2xl font-bold text-[#2B3210]">₦{(dashboardStats.total_outstanding || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Status Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4">
                <Clock size={32} className="text-amber-600" />
                <div>
                  <p className="text-amber-800 font-medium">Pending</p>
                  <p className="text-2xl font-bold text-amber-900">{dashboardStats.pending_invoices || 0}</p>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-4">
                <EnvelopeSimple size={32} className="text-blue-600" />
                <div>
                  <p className="text-blue-800 font-medium">Sent</p>
                  <p className="text-2xl font-bold text-blue-900">{dashboardStats.sent_invoices || 0}</p>
                </div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-4">
                <CheckCircle size={32} className="text-emerald-600" />
                <div>
                  <p className="text-emerald-800 font-medium">Paid</p>
                  <p className="text-2xl font-bold text-emerald-900">{dashboardStats.paid_invoices || 0}</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <h3 className="text-lg font-medium text-[#2B3210] mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                {isAdmin && (
                  <>
                    <button onClick={() => setOpenDialog('createEvent')} className="btn-primary flex items-center gap-2">
                      <Plus size={18} /> Create Event
                    </button>
                    <button onClick={() => setOpenDialog('createParticipant')} className="btn-secondary flex items-center gap-2">
                      <Users size={18} /> Add Participant
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Upcoming Transportation */}
            {dashboardStats.upcoming_transport > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <CarSimple size={24} className="text-purple-600" />
                  <div>
                    <p className="text-purple-800 font-medium">Upcoming Transportation</p>
                    <p className="text-sm text-purple-600">{dashboardStats.upcoming_transport} participants need pickup</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center flex-wrap gap-4">
              <h2 className="text-xl font-medium text-[#2B3210]">Events</h2>
              {isAdmin && (
                <button onClick={() => setOpenDialog('createEvent')} className="btn-primary flex items-center gap-2" data-testid="create-event-button">
                  <Plus size={18} /> Create Event
                </button>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Event Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Dates</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Participants</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {events.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-[#505631]">
                        No events found. {isAdmin && 'Create your first event to get started.'}
                      </td>
                    </tr>
                  ) : (
                    events.map(event => (
                      <tr 
                        key={event.id} 
                        className="hover:bg-[#FBF8EF] cursor-pointer transition-colors"
                        onClick={() => { setSelectedEvent(event); setOpenDialog('eventDetails'); }}
                      >
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-[#2B3210]">{event.name}</p>
                            {event.venue && <p className="text-xs text-[#505631]">{event.venue}</p>}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#505631]">
                          {event.start_date} <ArrowRight size={12} className="inline mx-1" /> {event.end_date}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#2B3210] font-medium">{event.participant_count || 0}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEventStatusColor(event.status)}`}>
                            {event.status}
                          </span>
                        </td>
                        <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-2">
                            {isAdmin && (
                              <>
                                {event.status !== 'closed' && (
                                  <>
                                    <button
                                      onClick={() => { setImportEventId(event.id); setOpenDialog('importParticipants'); }}
                                      className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                                      title="Import Participants"
                                    >
                                      <Upload size={18} />
                                    </button>
                                    <button
                                      onClick={() => handleSendAllMealLinks(event.id)}
                                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                                      title="Send All Meal Links"
                                    >
                                      <ForkKnife size={18} />
                                    </button>
                                    <button
                                      onClick={() => handleDownloadMealsReport(event.id)}
                                      className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                                      title="Meals Report PDF"
                                      data-testid={`meals-report-${event.id}`}
                                    >
                                      <FilePdf size={18} />
                                    </button>
                                    <button
                                      onClick={() => handleGenerateAllInvoices(event.id)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                      title="Generate & Review Invoices"
                                    >
                                      <Receipt size={18} />
                                    </button>
                                    <button
                                      onClick={() => { setEditingItem(event); setOpenDialog('editEvent'); }}
                                      className="p-2 text-[#B2560D] hover:bg-orange-50 rounded-lg"
                                      title="Edit"
                                    >
                                      <PencilSimple size={18} />
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => handleDeleteEvent(event.id)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                  title="Delete"
                                >
                                  <Trash size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Participants Tab */}
        <TabsContent value="participants">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9]">
              <div className="flex justify-between items-center flex-wrap gap-4 mb-4">
                <h2 className="text-xl font-medium text-[#2B3210]">Participants</h2>
                {isAdmin && (
                  <button onClick={() => setOpenDialog('createParticipant')} className="btn-primary flex items-center gap-2" data-testid="create-participant-button">
                    <Plus size={18} /> Add Participant
                  </button>
                )}
              </div>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#505631]" />
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-[#E5E2D9] rounded-lg text-sm"
                    />
                  </div>
                </div>
                <select
                  value={eventFilter}
                  onChange={(e) => setEventFilter(e.target.value)}
                  className="px-4 py-2.5 border border-[#E5E2D9] rounded-lg text-sm"
                >
                  <option value="">All Events</option>
                  {events.map(e => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2.5 border border-[#E5E2D9] rounded-lg text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Event</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Dates</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Meals</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Invoice</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {filteredParticipants.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-[#505631]">
                        No participants found.
                      </td>
                    </tr>
                  ) : (
                    filteredParticipants.map(p => (
                      <tr 
                        key={p.id} 
                        className="hover:bg-[#FBF8EF] cursor-pointer transition-colors"
                        onClick={() => { setSelectedParticipant(p); setOpenDialog('participantDetails'); }}
                      >
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-[#2B3210]">{p.first_name} {p.last_name}</p>
                            <p className="text-xs text-[#505631]">{p.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#505631]">{p.event_name}</td>
                        <td className="px-6 py-4 text-sm text-[#505631]">
                          {p.arrival_date} - {p.departure_date}
                        </td>
                        <td className="px-6 py-4">
                          {p.meal_selection_completed ? (
                            <span className="text-emerald-600"><CheckCircle size={18} /></span>
                          ) : (
                            <span className="text-amber-600"><Clock size={18} /></span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(p.invoice_status)}`}>
                            {p.invoice_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-[#2B3210]">
                          {p.outstanding_balance ? `₦${p.outstanding_balance.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-1">
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => handleSendMealLink(p.id)}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                  title="Send Meal Link"
                                >
                                  <ForkKnife size={16} />
                                </button>
                                <button
                                  onClick={() => handleGenerateInvoice(p.id)}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Generate Invoice"
                                >
                                  <Receipt size={16} />
                                </button>
                                <button
                                  onClick={() => handlePreviewInvoice(p.id)}
                                  className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded"
                                  title="Preview Invoice PDF"
                                  data-testid="preview-invoice-btn"
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  onClick={() => handleSendInvoice(p.id)}
                                  className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                                  title="Send Invoice"
                                >
                                  <EnvelopeSimple size={16} />
                                </button>
                                <button
                                  onClick={() => { setSelectedParticipant(p); setOpenDialog('recordPayment'); }}
                                  className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                                  title="Record Payment"
                                >
                                  <Money size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9]">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-medium text-[#2B3210]">Invoices</h2>
                  <p className="text-xs text-[#505631] mt-1 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    Auto-reminders: Daily at 9:00 AM UTC (3 days after sent, then every 7 days)
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Status Filter */}
                  <select
                    value={invoiceStatusFilter}
                    onChange={(e) => setInvoiceStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm bg-white"
                    data-testid="invoice-status-filter"
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                  </select>
                  {isAdmin && (
                    <button 
                      onClick={handleSendDueReminders}
                      className="btn-secondary flex items-center gap-2"
                      data-testid="send-reminders-btn"
                    >
                      <Bell size={18} /> Send Due Reminders Now
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Participant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Event</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Paid</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Outstanding</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Reminders</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {invoices.filter(inv => !invoiceStatusFilter || inv.status === invoiceStatusFilter).length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-8 text-center text-[#505631]">
                        {invoiceStatusFilter ? `No ${invoiceStatusFilter} invoices found.` : 'No invoices found.'}
                      </td>
                    </tr>
                  ) : (
                    invoices.filter(inv => !invoiceStatusFilter || inv.status === invoiceStatusFilter).map(inv => (
                      <tr key={inv.id} className="hover:bg-[#FBF8EF]">
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono font-medium text-[#B2560D]">{inv.invoice_number || '-'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-[#2B3210]">{inv.participant_name}</p>
                          <p className="text-xs text-[#505631]">{inv.participant_email}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#505631]">{inv.event_name}</td>
                        <td className="px-6 py-4 text-sm font-medium text-[#2B3210]">₦{inv.subtotal?.toLocaleString()}</td>
                        <td className="px-6 py-4 text-sm text-emerald-600">₦{inv.amount_paid?.toLocaleString()}</td>
                        <td className="px-6 py-4 text-sm font-medium text-red-600">
                          {inv.outstanding_balance > 0 ? `₦${inv.outstanding_balance.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(inv.status)}`}>
                            {inv.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {inv.status === 'sent' && isAdmin && (
                            <button
                              onClick={() => handleToggleReminders(inv.id)}
                              className={`p-1.5 rounded flex items-center gap-1 ${
                                inv.reminder_enabled !== false 
                                  ? 'text-emerald-600 hover:bg-emerald-50' 
                                  : 'text-gray-400 hover:bg-gray-50'
                              }`}
                              title={inv.reminder_enabled !== false ? 'Reminders ON - Click to disable' : 'Reminders OFF - Click to enable'}
                              data-testid="toggle-reminder-btn"
                            >
                              {inv.reminder_enabled !== false ? <Bell size={16} /> : <BellSlash size={16} />}
                              <span className="text-xs">{inv.reminder_count || 0} sent</span>
                            </button>
                          )}
                          {inv.status === 'pending' && <span className="text-xs text-gray-400">Not sent yet</span>}
                          {inv.status === 'paid' && <span className="text-xs text-gray-400">N/A</span>}
                        </td>
                        <td className="px-6 py-4">
                          {isAdmin && (
                            <div className="flex gap-1">
                              {inv.status !== 'paid' && (
                                <button
                                  onClick={() => { setEditingInvoice(inv); setOpenDialog('editInvoice'); }}
                                  className="p-1.5 text-[#B2560D] hover:bg-orange-50 rounded"
                                  title="Edit Invoice"
                                  data-testid="edit-invoice-btn"
                                >
                                  <PencilSimple size={16} />
                                </button>
                              )}
                              <button
                                onClick={() => handlePreviewInvoice(inv.participant_id)}
                                className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded"
                                title="Preview Invoice"
                              >
                                <Eye size={16} />
                              </button>
                              {inv.status !== 'paid' && (
                                <button
                                  onClick={() => handleResendInvoice(inv.participant_id)}
                                  className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                                  title="Resend Invoice"
                                >
                                  <EnvelopeSimple size={16} />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Transportation Tab */}
        <TabsContent value="transportation">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9]">
              <div className="flex justify-between items-center flex-wrap gap-4 mb-4">
                <h2 className="text-xl font-medium text-[#2B3210]">Transportation Bookings</h2>
                {isAdmin && events.filter(e => e.status === 'open').length > 0 && (
                  <select
                    onChange={(e) => e.target.value && handleGenerateTransportBookings(e.target.value)}
                    className="px-4 py-2 border border-[#B2560D] text-[#B2560D] rounded-lg text-sm"
                  >
                    <option value="">Generate Bookings for Event...</option>
                    {events.filter(e => e.status === 'open').map(e => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="flex gap-4">
                <select
                  value={transportEventFilter}
                  onChange={(e) => setTransportEventFilter(e.target.value)}
                  className="px-4 py-2.5 border border-[#E5E2D9] rounded-lg text-sm"
                >
                  <option value="">All Events</option>
                  {events.map(e => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Participant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Event</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Driver</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {filteredBookings.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-[#505631]">
                        No transport bookings. Select an event and click "Generate Bookings" to create them.
                      </td>
                    </tr>
                  ) : (
                    filteredBookings.map(booking => (
                      <tr key={booking.id} className="hover:bg-[#FBF8EF]">
                        <td className="px-6 py-4">
                          <p className="font-medium text-[#2B3210]">{booking.participant_name}</p>
                          <p className="text-xs text-[#505631]">{booking.participant_phone}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            booking.booking_type === 'arrival' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {booking.booking_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#2B3210]">{booking.booking_date}</td>
                        <td className="px-6 py-4 text-sm text-[#505631]">{booking.event_name}</td>
                        <td className="px-6 py-4">
                          {booking.driver_name ? (
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-[#2B3210]">{booking.driver_name}</span>
                              {booking.driver_phone && (
                                <button
                                  onClick={() => openWhatsApp(booking.driver_phone, booking)}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                                  title="WhatsApp Driver"
                                >
                                  <WhatsappLogo size={16} weight="fill" />
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-[#505631] italic">Unassigned</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            booking.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                            booking.status === 'assigned' ? 'bg-blue-100 text-blue-800' :
                            'bg-emerald-100 text-emerald-800'
                          }`}>
                            {booking.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            {isAdmin && booking.status !== 'completed' && (
                              <>
                                <select
                                  className="px-2 py-1 border border-[#E5E2D9] rounded text-xs"
                                  value={booking.driver_id || ''}
                                  onChange={(e) => e.target.value && handleAssignDriver(booking.id, e.target.value)}
                                >
                                  <option value="">Assign Driver</option>
                                  {drivers.filter(d => d.status === 'available').map(d => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                  ))}
                                </select>
                                {booking.status === 'assigned' && (
                                  <button
                                    onClick={() => handleCompleteBooking(booking.id)}
                                    className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                                    title="Mark Complete"
                                  >
                                    <Check size={16} />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Drivers Tab */}
        <TabsContent value="drivers">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
              <h2 className="text-xl font-medium text-[#2B3210]">Drivers</h2>
              {isAdmin && (
                <button onClick={() => setOpenDialog('createDriver')} className="btn-primary flex items-center gap-2" data-testid="create-driver-button">
                  <Plus size={18} /> Add Driver
                </button>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {drivers.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-[#505631]">
                        No drivers found. {isAdmin && 'Add drivers to assign them to transport bookings.'}
                      </td>
                    </tr>
                  ) : (
                    drivers.map(driver => (
                      <tr key={driver.id} className="hover:bg-[#FBF8EF]">
                        <td className="px-6 py-4 text-sm font-medium text-[#2B3210]">{driver.name}</td>
                        <td className="px-6 py-4 text-sm text-[#505631]">{driver.phone}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            driver.status === 'available' ? 'bg-emerald-100 text-emerald-800' :
                            driver.status === 'busy' ? 'bg-amber-100 text-amber-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {driver.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => { setEditingItem(driver); setOpenDialog('editDriver'); }}
                                  className="p-2 text-[#B2560D] hover:bg-orange-50 rounded-lg"
                                  title="Edit"
                                >
                                  <PencilSimple size={18} />
                                </button>
                                <button
                                  onClick={() => handleDeleteDriver(driver.id)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                  title="Delete"
                                >
                                  <Trash size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Rooms Tab */}
        <TabsContent value="rooms">
          <div className="space-y-6">
            {/* Room Controls */}
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-medium text-[#2B3210]">Room Allocation</h2>
                  <p className="text-sm text-[#505631]">Assign participants to rooms</p>
                </div>
                
                <div className="flex items-center gap-4">
                  <select
                    value={roomEventFilter}
                    onChange={(e) => setRoomEventFilter(e.target.value)}
                    className="px-4 py-2.5 border border-[#E5E2D9] rounded-lg text-sm min-w-[200px]"
                    data-testid="room-event-filter"
                  >
                    <option value="">Select an event...</option>
                    {events.map(e => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                  
                  {isAdmin && roomEventFilter && (
                    <>
                      <button
                        onClick={() => setOpenDialog('createRoom')}
                        className="btn-secondary flex items-center gap-2"
                        data-testid="create-room-btn"
                      >
                        <Plus size={18} /> Add Room
                      </button>
                      <button
                        onClick={() => setOpenDialog('bulkCreateRooms')}
                        className="btn-secondary flex items-center gap-2"
                      >
                        <Plus size={18} /> Bulk Create
                      </button>
                    </>
                  )}
                </div>
              </div>

              {roomEventFilter && isAdmin && (
                <div className="flex flex-wrap items-center gap-4 p-4 bg-[#FBF8EF] rounded-lg">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-[#2B3210]">Room Capacity:</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={roomConfig.room_capacity}
                      onChange={(e) => setRoomConfig({...roomConfig, room_capacity: parseInt(e.target.value) || 2})}
                      className="w-16 px-2 py-1 border border-[#E5E2D9] rounded text-center"
                    />
                  </div>
                  
                  <button
                    onClick={handleAutoAllocate}
                    disabled={allocating}
                    className="btn-primary flex items-center gap-2"
                    data-testid="auto-allocate-btn"
                  >
                    <MagicWand size={18} />
                    {allocating ? 'Allocating...' : 'Auto-Allocate'}
                  </button>
                  
                  <button
                    onClick={handleClearAllocations}
                    className="btn-secondary flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <ArrowsClockwise size={18} /> Clear All
                  </button>
                  
                  <button
                    onClick={handleDownloadRoomReport}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="room-report-btn"
                    title="Download Room Report PDF"
                  >
                    <FilePdf size={18} /> Report
                  </button>
                  
                  <div className="ml-auto text-sm text-[#505631]">
                    <span className="font-medium">{unassignedParticipants.length}</span> unassigned
                  </div>
                </div>
              )}
            </div>

            {!roomEventFilter ? (
              <div className="bg-white rounded-xl border border-[#E5E2D9] p-12 text-center">
                <Bed size={48} className="mx-auto text-[#505631] opacity-50 mb-4" />
                <p className="text-[#505631]">Select an event to manage room allocations</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Unassigned Participants Pool */}
                <div 
                  className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden"
                  onDragOver={handleDragOver}
                >
                  <div className="p-4 bg-[#FBF8EF] border-b border-[#E5E2D9]">
                    <h3 className="font-medium text-[#2B3210] flex items-center gap-2">
                      <Users size={18} />
                      Unassigned ({unassignedParticipants.length})
                    </h3>
                  </div>
                  <div className="p-4 max-h-[500px] overflow-y-auto space-y-2">
                    {unassignedParticipants.length === 0 ? (
                      <p className="text-sm text-[#505631] text-center py-4">All participants assigned</p>
                    ) : (
                      unassignedParticipants.map(p => (
                        <div
                          key={p.id}
                          draggable
                          onDragStart={() => handleDragStart(p)}
                          className="flex items-center gap-2 p-2 bg-[#FBF8EF] rounded-lg cursor-move hover:bg-orange-50 border border-transparent hover:border-[#B2560D] transition-colors"
                          data-testid={`unassigned-participant-${p.id}`}
                        >
                          <UserCircle size={20} className={p.gender === 'Male' ? 'text-blue-500' : p.gender === 'Female' ? 'text-pink-500' : 'text-gray-400'} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#2B3210] truncate">{p.name}</p>
                            <div className="flex items-center gap-2 text-xs text-[#505631]">
                              {p.gender && <span>{p.gender}</span>}
                              {p.is_couple && <span className="text-pink-600 font-medium">Couple</span>}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Rooms Grid */}
                <div className="lg:col-span-3">
                  {rooms.length === 0 ? (
                    <div className="bg-white rounded-xl border border-[#E5E2D9] p-12 text-center">
                      <Bed size={48} className="mx-auto text-[#505631] opacity-50 mb-4" />
                      <p className="text-[#505631]">No rooms created yet</p>
                      {isAdmin && (
                        <button
                          onClick={() => setOpenDialog('bulkCreateRooms')}
                          className="mt-4 btn-primary"
                        >
                          Create Rooms
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {rooms.map(room => (
                        <div
                          key={room.id}
                          className={`bg-white rounded-xl border overflow-hidden transition-colors ${
                            draggedParticipant && room.occupant_details.length < room.capacity 
                              ? 'border-[#B2560D] border-2 shadow-lg' 
                              : 'border-[#E5E2D9]'
                          }`}
                          onDragOver={handleDragOver}
                          onDrop={() => handleDropOnRoom(room.id, draggedParticipant?.room_id)}
                          data-testid={`room-${room.id}`}
                        >
                          <div className="p-3 bg-[#505631] text-white flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <Bed size={18} />
                              <span className="font-medium">{room.room_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                room.occupant_details.length >= room.capacity 
                                  ? 'bg-red-500' 
                                  : room.occupant_details.length > 0 
                                    ? 'bg-amber-500' 
                                    : 'bg-emerald-500'
                              }`}>
                                {room.occupant_details.length}/{room.capacity}
                              </span>
                              {isAdmin && (
                                <button
                                  onClick={() => handleDeleteRoom(room.id)}
                                  className="p-1 hover:bg-white/20 rounded"
                                  title="Delete Room"
                                >
                                  <Trash size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                          
                          <div className="p-3 min-h-[100px] space-y-2">
                            {room.occupant_details.length === 0 ? (
                              <p className="text-sm text-[#505631] text-center py-4 opacity-50">
                                Drop participants here
                              </p>
                            ) : (
                              room.occupant_details.map(occ => (
                                <div
                                  key={occ.id}
                                  draggable
                                  onDragStart={() => handleDragStart({...occ, room_id: room.id})}
                                  className="flex items-center gap-2 p-2 bg-[#FBF8EF] rounded-lg group cursor-move"
                                >
                                  <UserCircle 
                                    size={20} 
                                    className={occ.gender === 'Male' ? 'text-blue-500' : occ.gender === 'Female' ? 'text-pink-500' : 'text-gray-400'} 
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-[#2B3210] truncate">{occ.name}</p>
                                    <div className="flex items-center gap-2 text-xs text-[#505631]">
                                      {occ.gender && <span>{occ.gender}</span>}
                                      {occ.is_couple && <span className="text-pink-600 font-medium">Couple</span>}
                                    </div>
                                  </div>
                                  {isAdmin && (
                                    <button
                                      onClick={() => handleUnassignParticipant(occ.id)}
                                      className="p-1 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 rounded"
                                      title="Unassign"
                                    >
                                      <X size={14} />
                                    </button>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      
      {/* Create Event Dialog */}
      <Dialog open={openDialog === 'createEvent'} onOpenChange={(open) => setOpenDialog(open ? 'createEvent' : null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Event</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEvent} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Event Name</label>
              <input type="text" name="name" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Start Date</label>
                <input type="date" name="start_date" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">End Date</label>
                <input type="date" name="end_date" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Venue</label>
              <input type="text" name="venue" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Description</label>
              <textarea name="description" rows={3} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"></textarea>
            </div>
            
            {/* Bank Details Section */}
            <div className="pt-4 border-t border-[#E5E2D9]">
              <p className="text-sm font-medium text-[#505631] mb-3">Bank Details for Invoice (Optional)</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Account Name</label>
                  <input type="text" name="account_name" placeholder="e.g., Company Ltd" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" data-testid="event-account-name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Account Number</label>
                  <input type="text" name="account_number" placeholder="e.g., 1234567890" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" data-testid="event-account-number" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Bank Name</label>
                  <input type="text" name="bank_name" placeholder="e.g., First Bank" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" data-testid="event-bank-name" />
                </div>
              </div>
            </div>
            
            <button type="submit" className="btn-primary w-full">Create Event</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={openDialog === 'editEvent'} onOpenChange={(open) => { setOpenDialog(open ? 'editEvent' : null); if (!open) setEditingItem(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <form onSubmit={handleUpdateEvent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Event Name</label>
                <input type="text" name="name" defaultValue={editingItem.name} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Start Date</label>
                  <input type="date" name="start_date" defaultValue={editingItem.start_date} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">End Date</label>
                  <input type="date" name="end_date" defaultValue={editingItem.end_date} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Venue</label>
                <input type="text" name="venue" defaultValue={editingItem.venue} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Description</label>
                <textarea name="description" defaultValue={editingItem.description} rows={3} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"></textarea>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Status</label>
                <select name="status" defaultValue={editingItem.status} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              
              {/* Bank Details Section */}
              <div className="pt-4 border-t border-[#E5E2D9]">
                <p className="text-sm font-medium text-[#505631] mb-3">Bank Details for Invoice (Optional)</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Account Name</label>
                    <input type="text" name="account_name" defaultValue={editingItem.account_name || ''} placeholder="e.g., Company Ltd" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" data-testid="edit-event-account-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Account Number</label>
                    <input type="text" name="account_number" defaultValue={editingItem.account_number || ''} placeholder="e.g., 1234567890" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" data-testid="edit-event-account-number" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Bank Name</label>
                    <input type="text" name="bank_name" defaultValue={editingItem.bank_name || ''} placeholder="e.g., First Bank" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" data-testid="edit-event-bank-name" />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button type="button" onClick={() => { setOpenDialog(null); setEditingItem(null); }} className="flex-1 px-4 py-2.5 border border-[#E5E2D9] rounded-lg">Cancel</button>
                <button type="submit" className="flex-1 btn-primary">Save Changes</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Event Details Dialog */}
      <Dialog open={openDialog === 'eventDetails'} onOpenChange={(open) => { setOpenDialog(open ? 'eventDetails' : null); if (!open) setSelectedEvent(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Event Details</DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEventStatusColor(selectedEvent.status)}`}>
                  {selectedEvent.status}
                </span>
              </div>
              <div className="bg-[#FBF8EF] p-4 rounded-lg">
                <h3 className="text-xl font-semibold text-[#2B3210]">{selectedEvent.name}</h3>
                {selectedEvent.description && <p className="text-sm text-[#505631] mt-2">{selectedEvent.description}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-white border border-[#E5E2D9] p-4 rounded-lg">
                  <p className="text-[#505631]">Dates</p>
                  <p className="font-medium text-[#2B3210]">{selectedEvent.start_date} - {selectedEvent.end_date}</p>
                </div>
                <div className="bg-white border border-[#E5E2D9] p-4 rounded-lg">
                  <p className="text-[#505631]">Venue</p>
                  <p className="font-medium text-[#2B3210]">{selectedEvent.venue || 'Not specified'}</p>
                </div>
                <div className="bg-white border border-[#E5E2D9] p-4 rounded-lg">
                  <p className="text-[#505631]">Participants</p>
                  <p className="font-medium text-[#2B3210] text-lg">{selectedEvent.participant_count || 0}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Participant Dialog */}
      <Dialog open={openDialog === 'createParticipant'} onOpenChange={(open) => setOpenDialog(open ? 'createParticipant' : null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Participant</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateParticipant} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Event</label>
              <select name="event_id" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                <option value="">Select event...</option>
                {events.filter(e => e.status === 'open').map(e => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">First Name</label>
                <input type="text" name="first_name" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Last Name</label>
                <input type="text" name="last_name" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Email</label>
              <input type="email" name="email" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Phone</label>
              <input type="text" name="phone" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Arrival Date</label>
                <input type="date" name="arrival_date" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Departure Date</label>
                <input type="date" name="departure_date" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
            </div>
            
            {/* Gender and Couple fields for room allocation */}
            <div className="pt-3 border-t border-[#E5E2D9]">
              <p className="text-sm font-medium text-[#505631] mb-3">Room Allocation Info</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Gender</label>
                  <select name="gender" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" data-testid="participant-gender">
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" name="is_couple" id="is_couple" className="w-4 h-4" data-testid="participant-is-couple" />
                  <label htmlFor="is_couple" className="ml-2 text-sm text-[#2B3210]">Is Part of Couple</label>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input type="checkbox" name="needs_transportation" id="needs_transportation" className="w-4 h-4" />
              <label htmlFor="needs_transportation" className="text-sm text-[#2B3210]">Needs Transportation</label>
            </div>
            <button type="submit" className="btn-primary w-full">Add Participant</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Participant Details Dialog */}
      <Dialog open={openDialog === 'participantDetails'} onOpenChange={(open) => { setOpenDialog(open ? 'participantDetails' : null); if (!open) setSelectedParticipant(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Participant Details</DialogTitle>
          </DialogHeader>
          {selectedParticipant && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedParticipant.invoice_status)}`}>
                  {selectedParticipant.invoice_status}
                </span>
                {selectedParticipant.meal_selection_completed ? (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                    Meals Selected
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                    Meals Pending
                  </span>
                )}
                {selectedParticipant.needs_transportation && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    <CarSimple size={14} className="inline mr-1" /> Transport
                  </span>
                )}
              </div>
              
              <div className="bg-[#FBF8EF] p-4 rounded-lg">
                <h3 className="text-xl font-semibold text-[#2B3210]">{selectedParticipant.first_name} {selectedParticipant.last_name}</h3>
                <p className="text-sm text-[#505631]">{selectedParticipant.email}</p>
                <p className="text-sm text-[#505631]">{selectedParticipant.phone}</p>
                <div className="flex items-center gap-3 mt-2">
                  {selectedParticipant.gender && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${selectedParticipant.gender === 'Male' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                      {selectedParticipant.gender}
                    </span>
                  )}
                  {selectedParticipant.is_couple && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-700">
                      Couple
                    </span>
                  )}
                  {selectedParticipant.room_id && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#505631] text-white">
                      Room Assigned
                    </span>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-white border border-[#E5E2D9] p-4 rounded-lg">
                  <p className="text-[#505631]">Event</p>
                  <p className="font-medium text-[#2B3210]">{selectedParticipant.event_name}</p>
                </div>
                <div className="bg-white border border-[#E5E2D9] p-4 rounded-lg">
                  <p className="text-[#505631]">Stay Dates</p>
                  <p className="font-medium text-[#2B3210]">{selectedParticipant.arrival_date} - {selectedParticipant.departure_date}</p>
                </div>
              </div>
              
              {/* Financial Summary */}
              {selectedParticipant.invoice_amount && (
                <div className="bg-[#2B3210] text-white p-4 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm opacity-80">Invoice Total</p>
                      <p className="text-xl font-bold">₦{selectedParticipant.invoice_amount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm opacity-80">Amount Paid</p>
                      <p className="text-xl font-bold text-emerald-400">₦{selectedParticipant.amount_paid?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm opacity-80">Outstanding</p>
                      <p className="text-xl font-bold text-amber-400">₦{selectedParticipant.outstanding_balance?.toLocaleString()}</p>
                    </div>
                  </div>
                  {selectedParticipant.last_receipt_number && (
                    <div className="mt-3 pt-3 border-t border-white/20 text-center">
                      <p className="text-sm opacity-80">Last Receipt</p>
                      <p className="font-mono font-medium text-emerald-300">{selectedParticipant.last_receipt_number}</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Actions */}
              {isAdmin && (
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => handleSendMealLink(selectedParticipant.id)} className="btn-secondary flex items-center gap-2">
                    <ForkKnife size={18} /> Send Meal Link
                  </button>
                  <button onClick={() => handleGenerateInvoice(selectedParticipant.id)} className="btn-secondary flex items-center gap-2">
                    <Receipt size={18} /> Generate Invoice
                  </button>
                  <button onClick={() => handlePreviewInvoice(selectedParticipant.id)} className="btn-secondary flex items-center gap-2" data-testid="preview-invoice-detail-btn">
                    <Eye size={18} /> Preview Invoice
                  </button>
                  <button onClick={() => handleSendInvoice(selectedParticipant.id)} className="btn-secondary flex items-center gap-2">
                    <EnvelopeSimple size={18} /> Send Invoice
                  </button>
                  <button onClick={() => { setOpenDialog('recordPayment'); }} className="btn-primary flex items-center gap-2">
                    <Money size={18} /> Record Payment
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={openDialog === 'recordPayment'} onOpenChange={(open) => { setOpenDialog(open ? 'recordPayment' : null); if (!open) setSelectedParticipant(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {selectedParticipant && (
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="bg-[#FBF8EF] p-4 rounded-lg">
                <p className="text-sm text-[#505631]">Participant</p>
                <p className="font-medium text-[#2B3210]">{selectedParticipant.first_name} {selectedParticipant.last_name}</p>
                <p className="text-sm text-[#505631] mt-2">Outstanding Balance</p>
                <p className="font-bold text-lg text-[#B2560D]">₦{selectedParticipant.outstanding_balance?.toLocaleString() || '0'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Amount (₦)</label>
                <input type="number" name="amount" required min="1" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Payment Method</label>
                <select name="payment_method" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Reference (optional)</label>
                <input type="text" name="reference" placeholder="Transaction ID or receipt number" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <button type="submit" className="btn-primary w-full">Record Payment</button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={openDialog === 'editInvoice'} onOpenChange={(open) => { setOpenDialog(open ? 'editInvoice' : null); if (!open) setEditingInvoice(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Invoice</DialogTitle>
          </DialogHeader>
          {editingInvoice && (
            <form onSubmit={handleUpdateInvoice} className="space-y-4">
              <div className="bg-[#FBF8EF] p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-[#505631]">Invoice Number</p>
                    <p className="font-mono font-medium text-[#B2560D]">{editingInvoice.invoice_number || 'Not assigned'}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(editingInvoice.status)}`}>
                    {editingInvoice.status}
                  </span>
                </div>
                <p className="text-sm text-[#505631] mt-2">Participant</p>
                <p className="font-medium text-[#2B3210]">{editingInvoice.participant_name}</p>
              </div>
              
              <div className="border-t border-[#E5E2D9] pt-4">
                <p className="text-sm font-medium text-[#505631] mb-3">Room Charges</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Nights</label>
                    <input type="number" name="room_nights" defaultValue={editingInvoice.room_nights} min="0" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Rate (₦)</label>
                    <input type="number" name="room_rate" defaultValue={editingInvoice.room_rate} min="0" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                  </div>
                </div>
              </div>
              
              <div className="border-t border-[#E5E2D9] pt-4">
                <p className="text-sm font-medium text-[#505631] mb-3">Meal Charges</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Meal Count</label>
                    <input type="number" name="meal_count" defaultValue={editingInvoice.meal_count} min="0" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Rate (₦)</label>
                    <input type="number" name="meal_rate" defaultValue={editingInvoice.meal_rate} min="0" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                  </div>
                </div>
              </div>
              
              <div className="border-t border-[#E5E2D9] pt-4">
                <p className="text-sm font-medium text-[#505631] mb-3">Transportation</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      name="transportation_needed" 
                      id="edit_transportation_needed" 
                      defaultChecked={editingInvoice.transportation_needed}
                      className="w-4 h-4" 
                    />
                    <label htmlFor="edit_transportation_needed" className="text-sm text-[#2B3210]">Needs Transport</label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Rate (₦)</label>
                    <input type="number" name="transportation_rate" defaultValue={editingInvoice.transportation_rate} min="0" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                  </div>
                </div>
              </div>
              
              <div className="border-t border-[#E5E2D9] pt-4">
                <p className="text-sm font-medium text-[#505631] mb-3">Payment</p>
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Amount Paid (₦)</label>
                  <input type="number" name="amount_paid" defaultValue={editingInvoice.amount_paid} min="0" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                </div>
              </div>
              
              <div className="bg-[#2B3210] text-white p-4 rounded-lg">
                <div className="flex justify-between">
                  <span>Current Subtotal:</span>
                  <span className="font-bold">₦{editingInvoice.subtotal?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span>Outstanding:</span>
                  <span className="font-bold text-amber-400">₦{editingInvoice.outstanding_balance?.toLocaleString()}</span>
                </div>
                <p className="text-xs mt-2 opacity-70">Totals will be recalculated after saving</p>
              </div>
              
              <div className="flex gap-3">
                <button type="button" onClick={() => { setOpenDialog(null); setEditingInvoice(null); }} className="flex-1 px-4 py-2.5 border border-[#E5E2D9] rounded-lg">Cancel</button>
                <button type="submit" className="flex-1 btn-primary">Save & Recalculate</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Participants Dialog */}
      <Dialog open={openDialog === 'importParticipants'} onOpenChange={(open) => { setOpenDialog(open ? 'importParticipants' : null); if (!open) setImportEventId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Participants</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#FBF8EF] p-4 rounded-lg">
              <p className="text-sm font-medium text-[#2B3210] mb-2">CSV Format Required:</p>
              <p className="text-xs text-[#505631]">first_name, last_name, email, phone, arrival_date, departure_date, needs_transportation</p>
              <p className="text-xs text-[#505631] mt-2">Dates should be in YYYY-MM-DD format. Transportation should be "yes" or "no".</p>
            </div>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv"
                onChange={handleFileUpload}
                className="w-full"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Driver Dialog */}
      <Dialog open={openDialog === 'createDriver'} onOpenChange={(open) => setOpenDialog(open ? 'createDriver' : null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Driver</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateDriver} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Full Name</label>
              <input type="text" name="name" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Phone Number (with country code)</label>
              <input type="text" name="phone" required placeholder="e.g., 2348012345678" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              <p className="text-xs text-[#505631] mt-1">Include country code for WhatsApp notifications</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Status</label>
              <select name="status" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                <option value="available">Available</option>
                <option value="busy">Busy</option>
                <option value="off_duty">Off Duty</option>
              </select>
            </div>
            <button type="submit" className="btn-primary w-full">Add Driver</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Driver Dialog */}
      <Dialog open={openDialog === 'editDriver'} onOpenChange={(open) => { setOpenDialog(open ? 'editDriver' : null); if (!open) setEditingItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Driver</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <form onSubmit={handleUpdateDriver} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Full Name</label>
                <input type="text" name="name" defaultValue={editingItem.name} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Phone Number</label>
                <input type="text" name="phone" defaultValue={editingItem.phone} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Status</label>
                <select name="status" defaultValue={editingItem.status} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                  <option value="available">Available</option>
                  <option value="busy">Busy</option>
                  <option value="off_duty">Off Duty</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setOpenDialog(null); setEditingItem(null); }} className="flex-1 px-4 py-2.5 border border-[#E5E2D9] rounded-lg">Cancel</button>
                <button type="submit" className="flex-1 btn-primary">Save Changes</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Invoices Dialog */}
      <Dialog open={openDialog === 'reviewInvoices'} onOpenChange={(open) => { setOpenDialog(open ? 'reviewInvoices' : null); if (!open) { setReviewInvoices(null); setReviewEventId(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Review Invoices Before Sending</DialogTitle>
          </DialogHeader>
          {reviewInvoices && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Event Info & Summary */}
              <div className="bg-[#FBF8EF] p-4 rounded-lg mb-4">
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-[#2B3210]">{reviewInvoices.event.name}</h3>
                    <p className="text-sm text-[#505631]">{reviewInvoices.event.start_date} - {reviewInvoices.event.end_date}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-white px-4 py-2 rounded-lg border border-[#E5E2D9]">
                      <p className="text-2xl font-bold text-[#2B3210]">{reviewInvoices.summary.total_invoices}</p>
                      <p className="text-xs text-[#505631]">Total Invoices</p>
                    </div>
                    <div className="bg-white px-4 py-2 rounded-lg border border-[#E5E2D9]">
                      <p className="text-2xl font-bold text-amber-600">{reviewInvoices.summary.pending}</p>
                      <p className="text-xs text-[#505631]">Pending</p>
                    </div>
                    <div className="bg-white px-4 py-2 rounded-lg border border-[#E5E2D9]">
                      <p className="text-2xl font-bold text-[#2B3210]">₦{reviewInvoices.summary.total_amount?.toLocaleString()}</p>
                      <p className="text-xs text-[#505631]">Total Amount</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoices Table */}
              <div className="flex-1 overflow-auto border border-[#E5E2D9] rounded-lg">
                <table className="w-full">
                  <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9] sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Participant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Stay</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Room</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Meals</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Transport</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Paid</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Balance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200">
                    {reviewInvoices.invoices.map(inv => (
                      <tr key={inv.id} className="hover:bg-[#FBF8EF]">
                        <td className="px-4 py-3">
                          <p className="font-medium text-sm text-[#2B3210]">{inv.participant_name}</p>
                          <p className="text-xs text-[#505631]">{inv.participant_email}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#505631]">
                          {inv.arrival_date} - {inv.departure_date}
                          <br />
                          <span className="text-[#2B3210]">{inv.room_nights} nights</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#2B3210]">₦{inv.room_total?.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-[#2B3210]">₦{inv.meals_total?.toLocaleString()}</span>
                          <br />
                          <span className="text-xs text-[#505631]">{inv.meal_count} meals</span>
                          {!inv.meal_selection_completed && (
                            <span className="text-xs text-amber-600 block">⚠ Not selected</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#2B3210]">
                          {inv.transportation_needed ? `₦${inv.transportation_total?.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-[#2B3210]">₦{inv.subtotal?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-emerald-600">₦{inv.amount_paid?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm font-medium text-red-600">
                          {inv.outstanding_balance > 0 ? `₦${inv.outstanding_balance?.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(inv.status)}`}>
                            {inv.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-[#E5E2D9]">
                <div className="text-sm text-[#505631]">
                  <span className="font-medium">{reviewInvoices.summary.pending}</span> pending invoices will be sent
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={() => { setOpenDialog(null); setReviewInvoices(null); setReviewEventId(null); }}
                    className="px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                  >
                    Close
                  </button>
                  {reviewInvoices.summary.pending > 0 && (
                    <button 
                      onClick={handleSendAllInvoices}
                      className="btn-primary flex items-center gap-2"
                    >
                      <EnvelopeSimple size={18} />
                      Send All Pending Invoices ({reviewInvoices.summary.pending})
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Room Dialog */}
      <Dialog open={openDialog === 'createRoom'} onOpenChange={(open) => setOpenDialog(open ? 'createRoom' : null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Room</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateRoom} className="space-y-4">
            <input type="hidden" name="event_id" value={roomEventFilter} />
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Room Name</label>
              <input type="text" name="room_name" required placeholder="e.g., Room 101" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" data-testid="room-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Capacity</label>
              <input type="number" name="capacity" required defaultValue={2} min={1} max={10} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <button type="submit" className="btn-primary w-full">Add Room</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Create Rooms Dialog */}
      <Dialog open={openDialog === 'bulkCreateRooms'} onOpenChange={(open) => setOpenDialog(open ? 'bulkCreateRooms' : null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Create Rooms</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBulkCreateRooms} className="space-y-4">
            <input type="hidden" name="event_id" value={roomEventFilter} />
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Number of Rooms</label>
              <input type="number" name="count" required defaultValue={10} min={1} max={100} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Capacity per Room</label>
              <input type="number" name="capacity" required defaultValue={2} min={1} max={10} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Room Name Prefix</label>
              <input type="text" name="prefix" defaultValue="Room" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              <p className="text-xs text-[#505631] mt-1">Rooms will be named: Prefix 1, Prefix 2, etc.</p>
            </div>
            <button type="submit" className="btn-primary w-full">Create Rooms</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Events;
